import { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Col, Row } from "../../common/components/Grid";
import NumericInput from "../../common/components/NumericInput";
import ReactForm from "../../common/components/ReactForm";
import Select from "../../common/components/Select";
import TagManager from "../../common/components/TagManager";
import TextInput from "../../common/components/TextInput";
import TimeInput from "../../common/components/TimeInput";
import useClientConfig from "../../common/hooks/clientConfigHook";
import useCredentials from "../../common/hooks/credentialsHook";
import useNetwork from "../../common/hooks/networkHook";
import { ensureInteger } from "../../common/utils/generalUtils";
import { canCreateGeoFencingRule, canRemoveGeoFencingRule, canUpdateGeoFencingRule, insertGeoFenceRule, removeGeoFenceRule, updateGeoFenceRule, useGeoFencingRules } from "../../reducers/geoFencingSlice";
import ContactListDropdown from "../ContactListDropdown";
import ZoneDropdown from "../ZoneDropdown";
import GroupedSettings from "./GroupedSettings";
import PropTypes from "prop-types";

const defaultSort = ['alerttitle'];
const newRuleTemplate = { days: { 1: true, 2: true, 3: true, 4: true, 5: true, 6: true, 7: true } };


const GeoFencingDetails = forwardRef(({ value: rule, groupCtrl, disabled, onChange}, ref) => {
    const { t, i18n } = useTranslation();
    const days = useMemo(() => {
        const baseDate = new Date(Date.UTC(2021, 0, 4)); // January 4, 2021 is a Monday
        const days = [];

        for (let i = 0; i < 7; i++) {
            const day = new Date(baseDate);
            day.setDate(day.getDate() + i);
            days.push({ label: new Intl.DateTimeFormat(i18n.resolvedLanguage, { weekday: 'long' }).format(day), value: i + 1 });
        }

        return days;
    }, [i18n.resolvedLanguage]);

    return <ReactForm ref={ref}>
        <Row>
            <Col>
                <TextInput value={rule.alerttitle} maxLength={255} placeholder={t('title')} disabled={disabled} onChange={onChange('alerttitle')} required></TextInput>
            </Col>
        </Row>
        <Row>
            <Col>
                <TextInput value={rule.alertmessage} maxLength={1024} placeholder={t('message')} disabled={disabled} onChange={onChange('alertmessage')} required></TextInput>
            </Col>
        </Row>
        <Row>
            <Col md>
                <ContactListDropdown value={rule.list} placeholder={t('contactList', { count: 1 })} disabled={disabled} onChange={onChange('list')} required></ContactListDropdown>
            </Col>
            <Col md>
                <ZoneDropdown value={rule.zone} disabled={disabled} onChange={onChange('zone')} required></ZoneDropdown>
            </Col>
        </Row>
        <Row>
            <Col>
                {groupCtrl}
            </Col>
        </Row>
        <Row>
            <Col>
                <TagManager values={days} tags={Object.keys(rule?.days) ?? []} labelField="label" valueField="value" placeholder={t('day_other')} disabled={disabled} onChange={value => onChange('days')(value.reduce((obj, day) => { obj[day] = true; return obj; }, {}))}></TagManager>
            </Col>
        </Row>
        <Row>
            <Col>
                <TimeInput value={rule.start} placeholder={t('startTime')} disabled={disabled} onChange={onChange('start')}></TimeInput>
            </Col>
            <Col>
                <TimeInput value={rule.end} placeholder={t('endTime')} disabled={disabled} onChange={onChange('end')}></TimeInput>
            </Col>
        </Row>
        <Row>
            <Col md>
                <Select value={rule.comparison} placeholder={t('comparison')} disabled={disabled} onChange={onChange('comparison')} required labelField="label" valueField="value" options={[{ label: '<', value: '<' }, { label: '>', value: '>' }, { label: t('entry'), value: 'entry' },{ label: t('exit'), value: 'exit' }]}></Select>
            </Col>
            <Col md>
                { (rule.comparison === '<' || rule.comaprison === '>') && <NumericInput value={ensureInteger(rule.threshold)} placeholder={t('threshold')} onChange={onChange('count')} required></NumericInput> }
            </Col>
        </Row>
    </ReactForm>
})

const GeoFencing = ({ uuid, label, onClose }) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const network = useNetwork();
    const credentials = useCredentials();
    const rules = useGeoFencingRules();
    const sortFields = useMemo(() => ({
        alerttitle: t('title')
    }), [t]);
    const groupingCtrls = useMemo(() => {
        const ctrls = [{ type: 'type', label: t('type', { count: 1 }), title: t('type', { count: 1 }), field: ['site','level','type'], sortfield: 'type', strict: false, wildcard: t('anyWildcard'), uuid: 'b0a0be8a-db0f-4a77-ba47-5e20c58d4e5d' }];
        if (clientConfig.enableLevels) {
            ctrls.unshift({ type: 'level', label: t('level', { count: 1 }), title: t('level', { count: 1 }), field: ['site','level','type'], sortfield: 'level', strict: false, wildcard: t('anyWildcard'), uuid: '70733eb8-03c4-408a-8aaf-a60930954b79' });
        }
        if (clientConfig.enableSites) {
            ctrls.unshift({ type: 'site', label: t('site', { count: 1 }), title: t('site', { count: 1 }), field: ['site','level','type'], sortfield: 'site', strict: false, wildcard: t('anyWildcard'), uuid: '73f513f7-f041-4b67-b563-49d870468700' });
        }
        return ctrls;
    }, [t, clientConfig.enableLevels, clientConfig.enableSites]);

    return <GroupedSettings Details={GeoFencingDetails} data={rules} uuid={uuid} title={label} pkey="id"
        defaultEntry={newRuleTemplate} defaultSort={defaultSort} sortFields={sortFields}
        onClose={onClose} makeLabel={rule => <Row>
            <Col className="text-left">
                {rule.alerttitle}
            </Col>
        </Row>} initialGroupingField="title"
        groupingExclusions={['site','level','type','id']}
        groupingCtrls={groupingCtrls}
        canAdd={rule => canCreateGeoFencingRule(credentials, rule)} canUpdate={rule => canUpdateGeoFencingRule(credentials, rule)} canRemove={rule => canRemoveGeoFencingRule(credentials, rule)}
        onAdd={rule => {
            const toAdd = { ...rule };
            delete toAdd.id;
            insertGeoFenceRule(network, toAdd);
        }} onUpdate={delta => updateGeoFenceRule(network, delta)} onRemove={rule => removeGeoFenceRule(network, rule)}
        ></GroupedSettings>
}
GeoFencing.propTypes = {
    /**
     * ID to identify the settings status entry
     */
    uuid: PropTypes.string.isRequired,
    /**
     * Label for the screen
     */
    label: PropTypes.string.isRequired,
    /**
     * Function to call when the screen is closed
     */
    onClose: PropTypes.func
}

export default GeoFencing;